import React, { useState, useEffect } from 'react';
import { 
  Users, MessageSquare, Lock, Globe, CheckCircle2, XCircle, 
  Pause, Play, FileText, MessageCircle, GraduationCap, 
  Edit2, Plus, Trash2, Heart, Save
} from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import AdminTable from '../../components/admin/AdminTable';
import AdminCard from '../../components/admin/AdminCard';
import AdminModal from '../../components/admin/AdminModal';
import api from '../../services/api';
import { useProgram } from '../../contexts/ProgramContext';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import '../../styles/pages/admin/CommunityPage.css';
import '../../styles/admin-shared.css';

const CommunityPage = () => {
  // Re-fetch on program switch
  const { slug } = useProgram();
  const [groups, setGroups] = useState([]);
  const [forums, setForums] = useState([]);
  const [groupPosts, setGroupPosts] = useState([]);
  const [forumPosts, setForumPosts] = useState([]);
  const [mentors, setMentors] = useState([]);
  const [stats, setStats] = useState({
    totalGroups: 0,
    activeGroups: 0,
    totalForums: 0,
    totalGroupPosts: 0,
    totalForumPosts: 0,
    totalMentors: 0,
    totalMembers: 0
  });
  const [loading, setLoading] = useState(true);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showForumModal, setShowForumModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [editingForum, setEditingForum] = useState(null);
  const [activeTab, setActiveTab] = useState('groups');
  const [groupFormData, setGroupFormData] = useState({
    name: '',
    description: '',
    category: 'SUPPORT',
    is_private: false,
    is_active: true,
    icon: '',
    rules: ''
  });
  const [forumFormData, setForumFormData] = useState({
    name: '',
    description: '',
    category: 'Suporte Geral',
    is_active: true,
    icon: ''
  });
  const [forumPostFormData, setForumPostFormData] = useState({
    title: '',
    content: '',
    category: 'Suporte Geral',
    pinned: false
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [groupsData, forumsData, groupPostsData, forumPostsData, statsData] = await Promise.all([
        api.get('/admin/community/groups').catch(() => ({ data: { data: { groups: [] } } })),
        api.get('/admin/community/forums').catch(() => ({ data: { data: { forums: [] } } })),
        api.get('/admin/community/posts?type=group').catch(() => ({ data: { data: { posts: [] } } })),
        api.get('/admin/community/posts?type=forum').catch(() => ({ data: { data: { posts: [] } } })),
        api.get('/admin/community/stats').catch(() => ({ data: { data: {} } }))
      ]);

      setGroups(groupsData.data.data?.groups || []);
      setForums(forumsData.data.data?.forums || []);
      setGroupPosts(groupPostsData.data.data?.posts || []);
      setForumPosts(forumPostsData.data.data?.posts || []);
      setStats(statsData.data.data || {
        totalGroups: 0,
        activeGroups: 0,
        totalForums: 0,
        totalGroupPosts: 0,
        totalForumPosts: 0,
        totalMentors: 0,
        totalMembers: 0
      });
    } catch (error) {
      console.error('Erro ao carregar dados da comunidade:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (group = null) => {
    if (group) {
      setEditingGroup(group);
      setGroupFormData({
        name: group.name,
        description: group.description || '',
        category: group.category || 'SUPPORT',
        is_private: group.is_private || false,
        is_active: group.is_active ?? true,
        icon: group.icon || '👥',
        rules: Array.isArray(group.rules) ? group.rules.join('\n') : ''
      });
    } else {
      setEditingGroup(null);
      setGroupFormData({
        name: '',
        description: '',
        category: 'SUPPORT',
        is_private: false,
        is_active: true,
        icon: '',
        rules: ''
      });
    }
    setShowGroupModal(true);
  };

  const handleOpenForumModal = (forum = null) => {
    if (forum) {
      setEditingForum(forum);
      setForumFormData({
        name: forum.name,
        description: forum.description || '',
        category: forum.category || 'Suporte Geral',
        is_active: forum.is_active !== undefined ? forum.is_active : true,
        icon: forum.icon || '💬'
      });
    } else {
      setEditingForum(null);
      setForumFormData({
        name: '',
        description: '',
        category: 'Suporte Geral',
        is_active: true,
        icon: ''
      });
    }
    setShowForumModal(true);
  };

  const handleSaveGroup = async (e) => {
    e.preventDefault();
    try {
      const groupData = {
        ...groupFormData,
        rules: groupFormData.rules.split('\n').filter(r => r.trim())
      };

      if (editingGroup) {
        await api.put(`/admin/community/groups/${editingGroup.id}`, groupData);
      } else {
        await api.post('/admin/community/groups', groupData);
      }
      
      setShowGroupModal(false);
      setEditingGroup(null);
      fetchData();
    } catch (error) {
      console.error('Erro ao salvar grupo:', error);
      alert(error.response?.data?.message || 'Erro ao salvar grupo');
    }
  };

  const handleSaveForum = async (e) => {
    e.preventDefault();
    try {
      if (editingForum) {
        await api.put(`/admin/community/forums/${editingForum.id}`, forumFormData);
      } else {
        await api.post('/admin/community/forums', forumFormData);
      }
      
      setShowForumModal(false);
      setEditingForum(null);
      fetchData();
    } catch (error) {
      console.error('Erro ao salvar fórum:', error);
      alert(error.response?.data?.message || 'Erro ao salvar fórum');
    }
  };

  const handleDeleteForum = async (forumId) => {
    if (!window.confirm('Deseja realmente excluir este fórum?')) return;
    
    try {
      await api.delete(`/admin/community/forums/${forumId}`);
      fetchData();
    } catch (error) {
      console.error('Erro ao deletar fórum:', error);
      alert(error.response?.data?.message || 'Erro ao deletar fórum');
    }
  };

  const handleToggleForumStatus = async (forumId, currentStatus) => {
    try {
      await api.patch(`/admin/community/forums/${forumId}/status`, {
        is_active: !currentStatus
      });
      fetchData();
    } catch (error) {
      console.error('Erro ao alterar status do fórum:', error);
      alert(error.response?.data?.message || 'Erro ao alterar status');
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Deseja realmente excluir este grupo?')) return;
    
    try {
      await api.delete(`/admin/community/groups/${groupId}`);
      fetchData();
    } catch (error) {
      console.error('Erro ao excluir grupo:', error);
      alert(error.response?.data?.message || 'Erro ao excluir grupo');
    }
  };

  const handleToggleGroupStatus = async (groupId, currentStatus) => {
    try {
      await api.patch(`/admin/community/groups/${groupId}/status`, {
        is_active: !currentStatus
      });
      fetchData();
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      alert(error.response?.data?.message || 'Erro ao alterar status do grupo');
    }
  };

  const handleDeletePost = async (postId, postType) => {
    if (!window.confirm('Deseja realmente excluir este post?')) return;
    
    try {
      await api.delete(`/admin/community/posts/${postId}?type=${postType}`);
      fetchData();
    } catch (error) {
      console.error('Erro ao excluir post:', error);
      alert(error.response?.data?.message || 'Erro ao excluir post');
    }
  };

  const groupColumns = [
    {
      key: 'name',
      label: 'Grupo',
      render: (value, row) => (
        <div className="group-name-cell">
          <span className="group-icon"><Users size={20} /></span>
          <div>
            <div className="group-name">{value}</div>
            <div className="group-category">{row.category}</div>
          </div>
        </div>
      )
    },
    {
      key: 'member_count',
      label: 'Membros',
      render: (value) => `${value || 0} membros`
    },
    {
      key: 'is_private',
      label: 'Tipo',
      render: (value) => (
        <span className={`badge badge-${value ? 'warning' : 'info'}`}>
          {value ? <><Lock size={14} className="icon-mr" /> Privado</> : <><Globe size={14} className="icon-mr" /> Público</>}
        </span>
      )
    },
    {
      key: 'is_active',
      label: 'Status',
      render: (value) => (
        <span className={`badge badge-${value ? 'success' : 'danger'}`}>
          {value ? <><CheckCircle2 size={14} className="icon-mr" /> Ativo</> : <><XCircle size={14} className="icon-mr" /> Inativo</>}
        </span>
      )
    },
    {
      key: 'created_at',
      label: 'Criado em',
      render: (value) => format(new Date(value), 'dd/MM/yyyy', { locale: ptBR })
    }
  ];

  const forumColumns = [
    {
      key: 'name',
      label: 'Fórum',
      render: (value, row) => (
        <div className="group-name-cell">
          <span className="group-icon"><MessageSquare size={20} /></span>
          <div>
            <div className="group-name">{value}</div>
            <div className="group-category">{row.category}</div>
          </div>
        </div>
      )
    },
    {
      key: 'description',
      label: 'Descrição',
      render: (value) => value?.substring(0, 100) + (value?.length > 100 ? '...' : '')
    },
    {
      key: 'is_active',
      label: 'Status',
      render: (value) => (
        <span className={`badge badge-${value ? 'success' : 'danger'}`}>
          {value ? <><CheckCircle2 size={14} className="icon-mr" /> Ativo</> : <><XCircle size={14} className="icon-mr" /> Inativo</>}
        </span>
      )
    },
    {
      key: 'created_at',
      label: 'Criado em',
      render: (value) => format(new Date(value), 'dd/MM/yyyy', { locale: ptBR })
    }
  ];

  const postColumns = [
    {
      key: 'content',
      label: 'Post',
      render: (value, row) => (
        <div className="post-content-cell">
          {row.title && <div className="post-title">{row.title}</div>}
          <div className="post-text">{value.substring(0, 100)}...</div>
          <div className="post-meta">
            Por: {row.users?.name || 'Usuário'} • 
            {row.community_groups?.name ? ` Grupo: ${row.community_groups.name}` : 
             row.category ? ` Categoria: ${row.category}` : ''}
          </div>
        </div>
      )
    },
    {
      key: 'likes',
      label: 'Curtidas',
      render: (value) => <span className="flex items-center gap-1"><Heart size={14} /> {value || 0}</span>
    },
    {
      key: 'comments_count',
      label: 'Comentários',
      render: (value) => <span className="flex items-center gap-1"><MessageSquare size={14} /> {value || 0}</span>
    },
    {
      key: 'created_at',
      label: 'Publicado em',
      render: (value) => format(new Date(value), 'dd/MM/yyyy HH:mm', { locale: ptBR })
    }
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Carregando dados...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="community-page">
        {/* Header */}
        <div className="page-header">
          <div>
            <h1 className="flex items-center gap-2"><Users size={28} /> Comunidade</h1>
            <p>Gerencie grupos, fóruns e interações da comunidade</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="analytics-cards-grid">
          <AdminCard
            title="Total de Grupos"
            value={stats.totalGroups || 0}
            subtitle={`${stats.activeGroups || 0} ativos`}
            icon={<Users size={22} />}
            color="blue"
          />
          <AdminCard
            title="Posts de Grupos"
            value={stats.totalGroupPosts || groupPosts.length}
            subtitle="Publicações em grupos"
            icon={<FileText size={22} />}
            color="purple"
          />
          <AdminCard
            title="Posts do Fórum"
            value={stats.totalForumPosts || forumPosts.length}
            subtitle="Discussões no fórum"
            icon={<MessageCircle size={22} />}
            color="green"
          />
          <AdminCard
            title="Total de Membros"
            value={stats.totalMembers || 0}
            subtitle="Membros ativos"
            icon={<Heart size={22} />}
            color="yellow"
          />
        </div>

        {/* Tabs */}
        <div className="tabs-header">
          <div className="tabs-container">
            <button
              className={`tab-btn ${activeTab === 'groups' ? 'active' : ''}`}
              onClick={() => setActiveTab('groups')}
            >
              <Users size={15} /> Grupos ({groups.length})
            </button>
            <button
              className={`tab-btn ${activeTab === 'forums' ? 'active' : ''}`}
              onClick={() => setActiveTab('forums')}
            >
              <MessageSquare size={15} /> Fóruns ({forums.length})
            </button>
            <button
              className={`tab-btn ${activeTab === 'groupPosts' ? 'active' : ''}`}
              onClick={() => setActiveTab('groupPosts')}
            >
              <FileText size={15} /> Posts de Grupos ({groupPosts.length})
            </button>
            <button
              className={`tab-btn ${activeTab === 'forumPosts' ? 'active' : ''}`}
              onClick={() => setActiveTab('forumPosts')}
            >
              <MessageCircle size={15} /> Posts do Fórum ({forumPosts.length})
            </button>
            <button
              className={`tab-btn ${activeTab === 'mentors' ? 'active' : ''}`}
              onClick={() => setActiveTab('mentors')}
            >
              <GraduationCap size={15} /> Mentores ({mentors.length})
            </button>
          </div>
          <div className="tabs-actions">
            {activeTab === 'groups' && (
              <button 
                className="btn btn-primary btn-sm"
                onClick={() => handleOpenModal()}
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <Plus size={16} /> Novo Grupo
              </button>
            )}
            {activeTab === 'forums' && (
              <button 
                className="btn btn-primary btn-sm"
                onClick={() => handleOpenForumModal()}
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <Plus size={16} /> Novo Fórum
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        {activeTab === 'groups' && (
          <div className="table-section">
            <AdminTable
              columns={groupColumns}
              data={groups}
              loading={loading}
              actions={(row) => (
                <div className="table-actions">
                  <button 
                    className="table-action-btn edit"
                    onClick={() => handleOpenModal(row)}
                    title="Editar grupo"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button 
                    className={`table-action-btn ${row.is_active ? 'warning' : 'success'}`}
                    onClick={() => handleToggleGroupStatus(row.id, row.is_active)}
                    title={row.is_active ? 'Desativar' : 'Ativar'}
                  >
                    {row.is_active ? <Pause size={16} /> : <Play size={16} />}
                  </button>
                  <button 
                    className="table-action-btn delete"
                    onClick={() => handleDeleteGroup(row.id)}
                    title="Excluir grupo"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              )}
            />
          </div>
        )}

        {activeTab === 'forums' && (
          <div className="table-section">
            <AdminTable
              columns={forumColumns}
              data={forums}
              loading={loading}
              actions={(row) => (
                <div className="table-actions">
                  <button 
                    className="table-action-btn edit"
                    onClick={() => handleOpenForumModal(row)}
                    title="Editar fórum"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button 
                    className={`table-action-btn ${row.is_active ? 'warning' : 'success'}`}
                    onClick={() => handleToggleForumStatus(row.id, row.is_active)}
                    title={row.is_active ? 'Desativar' : 'Ativar'}
                  >
                    {row.is_active ? <Pause size={16} /> : <Play size={16} />}
                  </button>
                  <button 
                    className="table-action-btn delete"
                    onClick={() => handleDeleteForum(row.id)}
                    title="Excluir fórum"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              )}
            />
          </div>
        )}

        {activeTab === 'groupPosts' && (
          <div className="table-section">
            <div className="section-header">
              <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><FileText size={20} /> Publicações em Grupos</h3>
              <p>Posts criados pelos membros dentro dos grupos</p>
            </div>
            <AdminTable
              columns={postColumns}
              data={groupPosts}
              loading={loading}
              actions={(row) => (
                <button 
                  className="table-action-btn delete"
                  onClick={() => handleDeletePost(row.id, 'group')}
                  title="Excluir post"
                >
                  <Trash2 size={16} /> Excluir
                </button>
              )}
            />
          </div>
        )}

        {activeTab === 'forumPosts' && (
          <div className="table-section">
            <div className="section-header">
              <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><MessageCircle size={20} /> Discussões no Fórum</h3>
              <p>Posts públicos criados no fórum geral</p>
            </div>
            <AdminTable
              columns={postColumns}
              data={forumPosts}
              loading={loading}
              actions={(row) => (
                <button 
                  className="table-action-btn delete"
                  onClick={() => handleDeletePost(row.id, 'forum')}
                  title="Excluir post"
                >
                  <Trash2 size={16} /> Excluir
                </button>
              )}
            />
          </div>
        )}

        {activeTab === 'mentors' && (
          <div className="table-section">
            <div className="section-header">
              <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><GraduationCap size={20} /> Mentores</h3>
              <p>Usuários com papel de mentor na comunidade</p>
            </div>
            <div className="empty-state">
              <div className="empty-icon"><GraduationCap size={48} /></div>
              <h3>Sistema de Mentores</h3>
              <p>Funcionalidade em desenvolvimento. Em breve você poderá gerenciar mentores da comunidade.</p>
            </div>
          </div>
        )}

        {/* Group Modal */}
        <AdminModal
          isOpen={showGroupModal}
          onClose={() => {
            setShowGroupModal(false);
            setEditingGroup(null);
          }}
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {editingGroup ? <Edit2 size={24} /> : <Plus size={24} />}
              {editingGroup ? 'Editar Grupo' : 'Criar Novo Grupo'}
            </div>
          }
        >
          <form onSubmit={handleSaveGroup} className="group-form">
            <div className="form-group">
              <label htmlFor="name">Nome do Grupo *</label>
              <input
                type="text"
                id="name"
                value={groupFormData.name}
                onChange={(e) => setGroupFormData({ ...groupFormData, name: e.target.value })}
                placeholder="Ex: Suporte Mútuo, Novatos, Veteranos"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Descrição *</label>
              <textarea
                id="description"
                value={groupFormData.description}
                onChange={(e) => setGroupFormData({ ...groupFormData, description: e.target.value })}
                placeholder="Descreva o propósito e foco deste grupo..."
                rows="4"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="category">Categoria *</label>
                <select
                  id="category"
                  value={groupFormData.category}
                  onChange={(e) => setGroupFormData({ ...groupFormData, category: e.target.value })}
                  required
                >
                  <option value="SUPPORT">Suporte</option>
                  <option value="RECOVERY">Recuperação</option>
                  <option value="MOTIVATION">Motivação</option>
                  <option value="EDUCATION">Educação</option>
                  <option value="GENERAL">Geral</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="icon">Ícone</label>
                <input
                  type="text"
                  id="icon"
                  value={groupFormData.icon}
                  onChange={(e) => setGroupFormData({ ...groupFormData, icon: e.target.value })}
                  placeholder="Ex: 👥"
                  maxLength="5"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="rules">Regras do Grupo (uma por linha)</label>
              <textarea
                id="rules"
                value={groupFormData.rules}
                onChange={(e) => setGroupFormData({ ...groupFormData, rules: e.target.value })}
                placeholder="Respeite todos os membros&#10;Mantenha o anonimato&#10;Sem julgamentos"
                rows="5"
              />
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={groupFormData.is_private}
                  onChange={(e) => setGroupFormData({ ...groupFormData, is_private: e.target.checked })}
                />
                <span>Grupo privado (requer aprovação para entrar)</span>
              </label>
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={groupFormData.is_active}
                  onChange={(e) => setGroupFormData({ ...groupFormData, is_active: e.target.checked })}
                />
                <span>Grupo ativo (visível no app)</span>
              </label>
            </div>

            <div className="modal-actions">
              <button 
                type="button" 
                className="btn btn-outline"
                onClick={() => setShowGroupModal(false)}
              >
                Cancelar
              </button>
              <button type="submit" className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {editingGroup ? <><Save size={18} /> Salvar Alterações</> : <><Plus size={18} /> Criar Grupo</>}
              </button>
            </div>
          </form>
        </AdminModal>

        {/* Forum Modal */}
        <AdminModal
          isOpen={showForumModal}
          onClose={() => setShowForumModal(false)}
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {editingForum ? <Edit2 size={24} /> : <Plus size={24} />}
              {editingForum ? 'Editar Fórum' : 'Criar Novo Fórum'}
            </div>
          }
        >
          <form onSubmit={handleSaveForum} className="group-form">
            <div className="form-group">
              <label htmlFor="forumName">Nome do Fórum *</label>
              <input
                type="text"
                id="forumName"
                placeholder="Ex: Suporte para Iniciantes, Dicas Avançadas"
                value={forumFormData.name}
                onChange={(e) => setForumFormData({...forumFormData, name: e.target.value})}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="forumDescription">Descrição *</label>
              <textarea
                id="forumDescription"
                placeholder="Descreva o propósito e foco deste fórum..."
                value={forumFormData.description}
                onChange={(e) => setForumFormData({...forumFormData, description: e.target.value})}
                rows="4"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="forumCategory">Categoria *</label>
                <select
                  id="forumCategory"
                  value={forumFormData.category}
                  onChange={(e) => setForumFormData({...forumFormData, category: e.target.value})}
                  required
                >
                  <option value="Suporte Geral">Suporte Geral</option>
                  <option value="Primeiros Passos">Primeiros Passos</option>
                  <option value="Dicas e Estratégias">Dicas e Estratégias</option>
                  <option value="Histórias de Sucesso">Histórias de Sucesso</option>
                  <option value="Perguntas e Respostas">Perguntas e Respostas</option>
                  <option value="Recursos e Ferramentas">Recursos e Ferramentas</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="forumIcon">Ícone</label>
                <input
                  type="text"
                  id="forumIcon"
                  value={forumFormData.icon}
                  onChange={(e) => setForumFormData({...forumFormData, icon: e.target.value})}
                  placeholder="Ex: 💬"
                  maxLength="5"
                />
              </div>
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={forumFormData.is_active}
                  onChange={(e) => setForumFormData({...forumFormData, is_active: e.target.checked})}
                />
                <span>Fórum ativo (visível no app)</span>
              </label>
            </div>

            <div className="modal-actions">
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={() => setShowForumModal(false)}
              >
                Cancelar
              </button>
              <button type="submit" className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {editingForum ? <><Save size={18} /> Salvar Alterações</> : <><Plus size={18} /> Criar Fórum</>}
              </button>
            </div>
          </form>
        </AdminModal>
      </div>
    </AdminLayout>
  );
};

export default CommunityPage;
